/**
 * "Gói SAPA & tin Zalo" — chỉ chủ tiệm (quyền `xem.goi-sapa`, phản ánh 25/09 đợt 4).
 *
 *   - Gói SAPA: đang dùng thử / đã trả tới ngày nào; lịch sử trả tiền (sổ do người vận hành ghi, đợt 5).
 *   - Tin nhắn Zalo: trần tháng, đã tiêu, còn lại, cảnh báo 80%; tiêu vào loại tin nào; 30 tin gần nhất.
 *
 * Số "đã tiêu" là ĐÚNG con số worker dùng để quyết định gửi hay dừng (hàm SQL 0044) — chủ tiệm không
 * thấy một số, còn máy dừng theo số khác.
 */
import {
  formatVnDateFull,
  formatVnDateShort,
  formatVnTime,
  parseIsoStrict,
  toVnDateKey,
} from '@sapa/shared'
import {
  BANG_GIA_SAPA,
  chuoiVietQrSapa,
  noiDungChuyenKhoanSapa,
  SAPA_DANG_MIEN_PHI,
  TAI_KHOAN_NHAN_SAPA,
  thangDuocDungKhiDongNam,
  tienDongNamVnd,
} from '@sapa/shared/gia-sapa'
import { Copy, FileText, MessageSquareText, TriangleAlert } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { renderSVG } from 'uqr'
import {
  ApiFailure,
  type DieuKhoanCuaHangTrangThai,
  dieuKhoanCuaHangApi,
  duongDanMayChu,
  type GoiSapa,
  goiSapaApi,
  tienVnd,
  type ZbsSuDung,
} from './api.ts'
import { LienHeSapaKhoi } from './LienHeSapa.tsx'
import { GS } from './text/goi-sapa.ts'
import { ThongBao, Xuong } from './ui.tsx'

function ngayCua(iso: string): string {
  return toVnDateKey(parseIsoStrict(iso))
}

/**
 * Bảng giá + chuyển khoản (kế hoạch 10/2026, 5.6, ADR-094). Số tiền và tài khoản nhận lấy từ MỘT nguồn
 * (`@sapa/shared/gia-sapa`); mã VietQR dựng ngay trên máy từ `SAPA {mã tiệm}`. Không cổng thanh toán, không tự gia hạn.
 */
/** Mã QR chuyển khoản dựng ngay trên máy (uqr). */
function MaQrChuyenKhoan({ svg }: { svg: string }) {
  return (
    <div
      className="ck-ma-qr"
      role="img"
      aria-label={GS.ckAnhMaQr}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG do uqr tạo từ hằng số + mã tiệm do máy chủ trả, không có chữ người dùng gõ.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}

function TheBangGiaSapa({ maTiem }: { maTiem: string | null }) {
  const [bao, setBao] = useState<string | null>(null)
  const chuoi = maTiem === null ? null : chuoiVietQrSapa(maTiem)
  const noiDung = maTiem === null ? null : noiDungChuyenKhoanSapa(maTiem)
  const svg = useMemo(
    () =>
      chuoi === null
        ? null
        : renderSVG(chuoi, { ecc: 'M', border: 2, blackColor: '#1f1a1c', whiteColor: '#ffffff' }),
    [chuoi],
  )
  const g = BANG_GIA_SAPA
  const tien = (v: bigint) => tienVnd(v.toString())

  async function chep(chu: string) {
    try {
      await navigator.clipboard.writeText(chu)
      setBao(GS.ckDaChep)
    } catch {
      setBao(GS.ckKhongChep)
    }
  }

  return (
    <section className="the the-dem the-khoi bang-gia-sapa">
      <h2>{GS.giaTieuDe}</h2>
      <p className="chu-phu">{GS.giaGoiY}</p>
      <ul className="ds-bang-gia">
        <li>
          <b>{GS.giaThang(tien(g.giaThangNiemYetVnd))}</b>
          <span>{GS.giaNiemYet}</span>
        </li>
        <li>
          <b>{GS.giaThang(tien(g.giaThangThuKinVnd))}</b>
          <span>{GS.giaThuKin(g.thangGiuGiaThuKin, g.thangMienPhiThuKin)}</span>
        </li>
        <li>
          <b>{GS.giaNamTieuDe(g.thangDongNam, g.thangTangKhiDongNam)}</b>
          <span>
            {GS.giaNam(
              g.thangDongNam,
              thangDuocDungKhiDongNam(),
              tien(tienDongNamVnd(g.giaThangNiemYetVnd)),
              tien(tienDongNamVnd(g.giaThangThuKinVnd)),
            )}
          </span>
        </li>
        <li>
          <b>{GS.giaZaloTieuDe}</b>
          <span>{GS.giaZalo(tien(g.tinZaloNapToiThieuVnd))}</span>
        </li>
      </ul>

      <h3>{GS.ckTieuDe}</h3>
      {svg === null || noiDung === null ? (
        <p className="chu-phu">{GS.ckKhongCoMa}</p>
      ) : (
        <div className="ck-sapa">
          <p className="chu-phu">{GS.ckHuongDan}</p>
          <MaQrChuyenKhoan svg={svg} />
          <dl className="ck-thong-tin">
            <div>
              <dt>{GS.ckNganHang}</dt>
              <dd>{TAI_KHOAN_NHAN_SAPA.nganHang}</dd>
            </div>
            <div>
              <dt>{GS.ckSoTk}</dt>
              <dd>
                <b>{TAI_KHOAN_NHAN_SAPA.soTaiKhoan}</b>
                <button
                  type="button"
                  className="nut nut-vien ck-chep"
                  onClick={() => void chep(TAI_KHOAN_NHAN_SAPA.soTaiKhoan)}
                >
                  <Copy size={18} />
                  {GS.ckChep}
                </button>
              </dd>
            </div>
            <div>
              <dt>{GS.ckChuTk}</dt>
              <dd>{TAI_KHOAN_NHAN_SAPA.chuTaiKhoan}</dd>
            </div>
            <div>
              <dt>{GS.ckNoiDung}</dt>
              <dd>
                <b>{noiDung}</b>
                <button
                  type="button"
                  className="nut nut-vien ck-chep"
                  onClick={() => void chep(noiDung)}
                >
                  <Copy size={18} />
                  {GS.ckChep}
                </button>
              </dd>
            </div>
          </dl>
          {bao !== null && (
            <p className="chu-phu" role="status">
              {bao}
            </p>
          )}
          <p className="chu-phu">{GS.ckSauKhiChuyen}</p>
        </div>
      )}
    </section>
  )
}

export function TheGoiSapa({ onLoi }: { onLoi: (e: unknown) => void }) {
  const [g, setG] = useState<GoiSapa | null>(null)
  // biome-ignore lint/correctness/useExhaustiveDependencies: tải MỘT lần khi mở thẻ.
  useEffect(() => {
    goiSapaApi.doc().then(setG).catch(onLoi)
  }, [])
  if (g === null) return <Xuong cao={120} />
  return (
    <>
      <section className="the the-dem the-khoi">
        <h2>{GS.goiTieuDe}</h2>
        {SAPA_DANG_MIEN_PHI ? (
          // SAPA đang miễn phí (ADR-113): không "Dùng thử đến…", không "Đã trả đến…", không "còn N ngày". Tạm ngưng thì vẫn nói.
          <>
            <p className="goi-sapa-han">
              <b>
                {g.tinhTrang === 'tam-ngung'
                  ? GS.tinhTrang('tam-ngung', 'tam-ngung')
                  : GS.mienPhiTinhTrang}
              </b>
            </p>
            <p className="chu-phu">
              {g.tinhTrang === 'tam-ngung' ? GS.tamNgungHuongDan : GS.mienPhiTinZalo}
            </p>
          </>
        ) : (
          <>
            <p className="goi-sapa-han">
              <b>{GS.tinhTrang(g.tinhTrang, g.canhBao.muc)}</b>
              <span className="chu-phu">
                {g.dungThuDen !== null
                  ? GS.dungThuDen(formatVnDateFull(ngayCua(g.dungThuDen)))
                  : g.daTraDen !== null
                    ? GS.daTraDen(formatVnDateFull(ngayCua(g.daTraDen)))
                    : GS.khongHan}
                {g.canhBao.ngayConLai !== null &&
                  g.canhBao.muc !== 'on' &&
                  ` · ${GS.conNgay(g.canhBao.ngayConLai)}`}
              </span>
            </p>
            <p className="chu-phu">{GS.goiGiaHan}</p>
          </>
        )}
        <LienHeSapaKhoi kieu="cau" />
        {g.thanhToan.length > 0 && (
          <>
            <h3>{GS.lichSuTra}</h3>
            <ul className="ds-muc ds-tra-tien">
              {g.thanhToan.map((t) => (
                <li key={t.id} className={`muc ${t.daDao ? 'mo-di' : ''}`}>
                  <div className="chu">
                    <b>
                      {t.goi} · {GS.soThang(t.soThang)}
                    </b>
                    <span>
                      {formatVnDateShort(ngayCua(t.tu))} → {formatVnDateShort(ngayCua(t.den))}
                      {t.daDao && ` · ${GS.daDao}`}
                    </span>
                  </div>
                  <b className="so-tien">{tienVnd(t.soTienVnd)}</b>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
      {!SAPA_DANG_MIEN_PHI && <TheBangGiaSapa maTiem={g.maTiem ?? null} />}
    </>
  )
}

export function TheZbsSuDung({ onLoi }: { onLoi: (e: unknown) => void }) {
  const [thang, setThang] = useState<string | null>(null)
  const [d, setD] = useState<ZbsSuDung | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  useEffect(() => {
    let con = true
    setLoi(null)
    goiSapaApi
      .zbs(thang)
      .then((x) => {
        if (con) setD(x)
      })
      .catch((e: unknown) => {
        if (!con) return
        if (e instanceof ApiFailure) setLoi(e.messageVi)
        else onLoi(e)
      })
    return () => {
      con = false
    }
  }, [thang, onLoi])

  if (loi !== null) return <ThongBao loai="loi">{loi}</ThongBao>
  if (d === null) return <Xuong cao={200} />
  // Phần trăm để vẽ thanh — tính bằng BigInt (tiền không đi qua Number), kẹp 0..100.
  const phanTram =
    d.tranVnd === null || d.tranVnd === '0'
      ? '0'
      : (() => {
          const p = (BigInt(d.daTieuVnd) * 100n) / BigInt(d.tranVnd)
          return (p > 100n ? 100n : p).toString()
        })()
  return (
    <section className="the the-dem the-khoi">
      <div className="the-tieu-de">
        <div>
          <h2>
            <MessageSquareText size={20} /> {GS.zbsTieuDe}
          </h2>
          <p className="chu-phu">
            {d.tiemDong === true
              ? SAPA_DANG_MIEN_PHI
                ? GS.zbsTiemDongMienPhi
                : GS.zbsTiemDong
              : d.dangBat
                ? GS.zbsDangBat
                : GS.zbsChuaBat}
          </p>
        </div>
        <select
          className="o-nhap chon-thang"
          aria-label={GS.chonThang}
          value={d.thang}
          onChange={(e) => {
            setD(null)
            setThang(e.target.value)
          }}
        >
          {d.cacThang.map((t) => (
            <option key={t} value={t}>
              {GS.thang(t)}
            </option>
          ))}
        </select>
      </div>

      {d.laThangNay && d.tranVnd !== null ? (
        <div className="ngan-sach-zbs" data-muc={d.het ? 'het' : d.dung80 ? 'gan-het' : 'on'}>
          <div className="hang-so">
            <span>
              {GS.daTieu} <b>{tienVnd(d.daTieuVnd)}</b>
            </span>
            <span>
              {GS.tran} <b>{tienVnd(d.tranVnd)}</b>
            </span>
          </div>
          <div className="thanh-ngan-sach" aria-hidden="true">
            <span style={{ width: `${phanTram}%` }} />
          </div>
          <p>
            {GS.conLai} <b>{tienVnd(d.conLaiVnd ?? '0')}</b>
            {d.tranMacDinh && <span className="chu-phu"> · {GS.tranMacDinh}</span>}
          </p>
          {(d.dung80 || d.het) && (
            <p className="canh-bao-zbs">
              <TriangleAlert size={18} />
              {d.het ? GS.het : GS.gan80}
            </p>
          )}
        </div>
      ) : (
        <p>
          {GS.daTieuThangCu(GS.thang(d.thang))} <b>{tienVnd(d.daTieuVnd)}</b>
        </p>
      )}

      <h3>{GS.theoLoai}</h3>
      {d.theoMau.length === 0 ? (
        <p className="chu-phu">{GS.chuaCoTin}</p>
      ) : (
        <table className="bang-zbs">
          <thead>
            <tr>
              <th scope="col">{GS.cotLoai}</th>
              <th scope="col">{GS.cotSoTin}</th>
              <th scope="col">{GS.cotTien}</th>
            </tr>
          </thead>
          <tbody>
            {d.theoMau.map((m) => (
              <tr key={m.mau}>
                <th scope="row">{m.tenVi}</th>
                <td>{m.soTin}</td>
                <td>{tienVnd(m.tienVnd)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(d.dem.boQuaNganSach > 0 || d.dem.boQuaDongY > 0 || d.dem.thatBai > 0) && (
        <ul className="dem-zbs">
          {d.dem.boQuaNganSach > 0 && <li>{GS.boQuaNganSach(d.dem.boQuaNganSach)}</li>}
          {d.dem.boQuaDongY > 0 && <li>{GS.boQuaDongY(d.dem.boQuaDongY)}</li>}
          {d.dem.thatBai > 0 && <li>{GS.thatBai(d.dem.thatBai)}</li>}
        </ul>
      )}

      {d.gan.length > 0 && (
        <details className="tin-gan-zbs">
          <summary>{GS.tinGan(d.gan.length)}</summary>
          <ul className="ds-muc">
            {d.gan.map((t, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: danh sách chỉ đọc, thứ tự cố định theo giờ.
              <li key={i} className="muc">
                <div className="chu">
                  <b>{t.tenVi}</b>
                  <span>
                    {formatVnTime(parseIsoStrict(t.luc))} {formatVnDateShort(ngayCua(t.luc))} ·{' '}
                    {t.nguoiNhan} · {GS.trangThai(t.trangThai)}
                  </span>
                </div>
                <span className="so-tien">{tienVnd(t.tienVnd)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

/**
 * Dải báo trên đầu màn của chủ tiệm khi dùng thử / kỳ đã trả sắp hết, đang ân hạn, đã hết, tạm ngưng.
 * Trước đây API trả hạn dùng mà không màn nào hiện — tiệm biết hết hạn khi khách báo không đặt được.
 */
export function DaiBaoHan({ onMo }: { onMo: () => void }) {
  const [g, setG] = useState<GoiSapa | null>(null)
  useEffect(() => {
    goiSapaApi
      .doc()
      .then(setG)
      .catch(() => undefined)
  }, [])
  // Đang miễn phí thì chỉ tạm ngưng mới có dải (máy chủ đã trả 'on'; chốt thêm ở máy khách).
  if (g === null || g.canhBao.muc === 'on' || (SAPA_DANG_MIEN_PHI && g.canhBao.muc !== 'tam-ngung'))
    return null
  return (
    <div className="dai-bao-han" data-muc={g.canhBao.muc} role="status">
      <TriangleAlert size={18} />
      <span>{GS.dai(g.canhBao.muc, g.canhBao.ngayConLai)}</span>
      <button type="button" className="nut-lien-ket" onClick={onMo}>
        {GS.daiXem}
      </button>
    </div>
  )
}

/**
 * Dải "SAPA cập nhật điều khoản" (kế hoạch 10/2026, 5.4, ADR-094): chủ tiệm có sẵn thấy tới khi bấm "Đồng ý" bản hiện
 * hành. Cờ tắt (mặc định) hoặc đã đồng ý → không hiện gì. Đồng ý ghi `audit_log` `tenant.terms_accepted` kèm phiên bản.
 */
export function DaiDieuKhoan() {
  const [t, setT] = useState<DieuKhoanCuaHangTrangThai | null>(null)
  const [dang, setDang] = useState(false)
  const [xong, setXong] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  useEffect(() => {
    dieuKhoanCuaHangApi
      .doc()
      .then(setT)
      .catch(() => undefined)
  }, [])
  if (xong) {
    return (
      <div className="dai-dieu-khoan" role="status">
        <FileText size={20} />
        <span>{GS.dkDaGhi}</span>
      </div>
    )
  }
  if (t === null || !t.bat || t.daDongY !== false || t.phienBan === undefined) return null
  const phienBan = t.phienBan
  async function dongY() {
    setDang(true)
    setLoi(null)
    try {
      await dieuKhoanCuaHangApi.dongY(phienBan)
      setXong(true)
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : 'Chưa lưu được. Bạn thử lại nhé.')
    } finally {
      setDang(false)
    }
  }
  return (
    <div className="dai-dieu-khoan" role="status">
      <FileText size={20} />
      <span>{GS.dkCapNhat}</span>
      <div className="dai-dieu-khoan-nut">
        {t.duongDan !== undefined && (
          <a
            className="nut nut-vien"
            href={duongDanMayChu(t.duongDan)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {GS.dkDoc}
          </a>
        )}
        <button
          type="button"
          className="nut nut-chinh"
          disabled={dang}
          onClick={() => void dongY()}
        >
          {dang ? GS.dkDangGhi : GS.dkDongY}
        </button>
      </div>
      {loi !== null && <span className="chu-loi">{loi}</span>}
    </div>
  )
}
